import {
  ThemeChangeTimer
} from "./chunk-RARANVEZ.js";

// lib/context/context.tsx
import {
  DarkMode,
  DarkModeOutlined,
  LightMode,
  LightModeOutlined,
  ThunderstormOutlined
} from "@mui/icons-material";
import {
  CssBaseline,
  ThemeProvider as MuiThemeProvider,
  createTheme
} from "@mui/material";
import {
  createContext,
  useEffect,
  useLayoutEffect,
  useState,
  useCallback
} from "react";

// lib/context/logger.ts
var debug = true;
var debugContext = (...msg) => {
  if (debug) {
    console.debug(`[${name}] `, ...msg);
  }
};

// styles/root.css
(function() {
  if (!document.getElementById("2287f604ece51372d6b1130395f086264520c3e773553929ca75f3f060ddf259")) {
    var e = document.createElement("style");
    e.id = "2287f604ece51372d6b1130395f086264520c3e773553929ca75f3f060ddf259";
    e.textContent = `@import url('https://fonts.googleapis.com/css2?family=Roboto&display=swap');

html {
  box-sizing: border-box;
}

*,
*:before,
*:after {
  box-sizing: inherit;
}

HTML,
BODY {
  font-family: Roboto;
  height: 100%;
  margin: 0;
  overflow: hidden;
}

#root {
  display: flex;
  height: 100%;
  width: 100%;
  flex-direction: column;
}

A {
  color: dodgerblue;
}

body {
  --scrollbar-width: calc(100vw - 100%);
}
`;
    document.head.appendChild(e);
  }
})();

// lib/context/context.tsx
import { jsx, jsxs } from "react/jsx-runtime";
var timestamp = (date) => date.hour * 3600 + date.minute * 60 + date.second;
var KEEP_MODE_FOR_MS = 4 * 60 * 60 * 1e3;
var RESET_MODE_ANIM_MS = 3 * 1e3;
var DEFAULT_GOING_DARK_TIME = {
  hour: 17,
  minute: 0,
  second: 0
};
var DEFAULT_GOING_LIGHT_TIME = {
  hour: 6,
  minute: 0,
  second: 0
};
var StorageFields = {
  MODE: "mode",
  MODE_CHANGED: "modeChanged"
};
var isDark = () => localStorage.getItem(StorageFields.MODE) === "dark";
var emptyContext = {
  mode: "light",
  icon: null,
  text: "",
  set: () => {
    alert("Not connected to a provider");
  },
  toggle: () => {
    alert("Not connected to a provider");
  },
  reset: () => {
    alert("Not connected to a provider");
  }
};
var Theme = createContext({ ...emptyContext });
var ThemeProvider = ({
  name: name2 = "default",
  forceMode,
  children,
  onChangeMode,
  context = Theme,
  goingLightTime = DEFAULT_GOING_LIGHT_TIME,
  goingDarkTime = DEFAULT_GOING_DARK_TIME,
  tickIntervalMs
}) => {
  const [mode, setMode] = useState(isDark() ? "dark" : "light");
  const [resetting, setResetting] = useState(false);
  const Provider = context.Provider;
  const theme = createTheme({
    palette: {
      mode
    }
  });
  const toggleMode = (newMode) => {
    localStorage.setItem(StorageFields.MODE, newMode);
    localStorage.setItem(StorageFields.MODE_CHANGED, Date.now().toString());
    debugContext("Changing mode. New mode:", newMode);
  };
  const startupMode = useCallback(() => {
    const now = /* @__PURE__ */ new Date();
    const nowStamp = timestamp({
      hour: now.getHours(),
      minute: now.getMinutes(),
      second: now.getSeconds()
    });
    const goingDarkTimeStamp = timestamp(goingDarkTime);
    const goingLightTimeStamp = timestamp(goingLightTime);
    if (goingDarkTimeStamp > goingLightTimeStamp) {
      const newMode = nowStamp > goingLightTimeStamp && nowStamp < goingDarkTimeStamp ? "light" : "dark";
      debugContext("Dark happens later", newMode);
      return newMode;
    } else {
      const newMode = nowStamp > goingDarkTimeStamp && nowStamp < goingLightTimeStamp ? "dark" : "light";
      debugContext("Light happens later", newMode);
      return newMode;
    }
  }, [goingDarkTime, goingLightTime]);
  const changeMode = useCallback(
    (newMode, override) => {
      if (override === "set" && newMode !== null) {
        toggleMode(newMode);
        setMode(newMode);
        if (onChangeMode !== void 0) {
          onChangeMode(newMode);
        }
        return;
      }
      if (override === "clear") {
        localStorage.removeItem(StorageFields.MODE);
        localStorage.removeItem(StorageFields.MODE_CHANGED);
        const originalMode = startupMode();
        setMode(originalMode);
        if (onChangeMode !== void 0) {
          onChangeMode(originalMode);
        }
      }
    },
    [onChangeMode, startupMode]
  );
  const overriddenMode = () => {
    const storedChange = localStorage.getItem(StorageFields.MODE_CHANGED);
    if (storedChange !== null) {
      const lastUpdated = parseInt(storedChange);
      if (lastUpdated + KEEP_MODE_FOR_MS > Date.now()) {
        const storedMode = localStorage.getItem(StorageFields.MODE);
        const mode2 = ["light", null].includes(storedMode) ? "light" : "dark";
        debugContext("Keeping overridden mode", mode2);
        return mode2;
      }
    }
    return null;
  };
  const updateModeOnStartup = useCallback(() => {
    if (overriddenMode() === null) {
      debugContext("(May be) Overriding state based on time");
      changeMode(startupMode(), "clear");
    }
  }, [changeMode, startupMode]);
  useEffect(() => {
    debugContext("Theme mode", mode, forceMode);
  }, [forceMode, mode]);
  useLayoutEffect(() => {
    if (forceMode !== void 0) {
      debugContext("Forcing mode", forceMode);
      setMode(forceMode);
    }
  }, [forceMode]);
  useLayoutEffect(() => {
    if (forceMode === void 0) {
      updateModeOnStartup();
    }
  }, [forceMode, updateModeOnStartup]);
  const contextHandler = {
    mode,
    icon: resetting ? /* @__PURE__ */ jsx(ThunderstormOutlined, {}) : overriddenMode() === null ? mode === "dark" ? /* @__PURE__ */ jsx(DarkModeOutlined, {}) : /* @__PURE__ */ jsx(LightModeOutlined, {}) : mode === "dark" ? /* @__PURE__ */ jsx(DarkMode, {}) : /* @__PURE__ */ jsx(LightMode, {}),
    text: resetting ? "Resetting" : mode === "dark" ? "Dark mode" : "Light mode",
    set: (newMode) => {
      changeMode(newMode, "set");
    },
    toggle: () => {
      const newMode = mode === "light" ? "dark" : "light";
      changeMode(newMode, "set");
    },
    reset: (e) => {
      debugContext("Resetting theme");
      e.preventDefault();
      setResetting(true);
      setTimeout(() => {
        debugContext("Applying reset");
        changeMode(null, "clear");
        setResetting(false);
      }, RESET_MODE_ANIM_MS);
    }
  };
  return /* @__PURE__ */ jsxs(Provider, { value: contextHandler, children: [
    /* @__PURE__ */ jsx(
      ThemeChangeTimer,
      {
        name: name2,
        every: goingLightTime,
        onAlarm: () => {
          changeMode("light", "clear");
        },
        tickIntervalMs
      }
    ),
    /* @__PURE__ */ jsx(
      ThemeChangeTimer,
      {
        name: name2,
        every: goingDarkTime,
        onAlarm: () => {
          changeMode("dark", "clear");
        },
        tickIntervalMs
      }
    ),
    /* @__PURE__ */ jsxs(MuiThemeProvider, { theme, children: [
      /* @__PURE__ */ jsx(CssBaseline, {}),
      children
    ] })
  ] });
};

export {
  emptyContext,
  Theme,
  ThemeProvider
};
